












































import { Component, Inject, Vue, Prop } from "vue-property-decorator";
import { ImportStatusCodes, ImportStepCodes } from "@/data/enums/imports";
import {
  IImportProviderData,
  IImportProviderMethods,
  IImportStep
} from "@/models/imports";
import { CreateElement } from "vue";

// Components
import AffiliatesProvider from "@/components/app/global/affiliate/affiliatesProvider.vue";
import AffiliatesTable from "@/components/app/global/affiliate/affiliatesTable.vue";
import BrandPaymentMethodsList from "@/components/app/global/payments/brandPaymentMethodsList.vue";
import ClientSearch from "@/components/app/global/client/clientSearch.vue";
import LegacyInvoicesProvider from "@/components/app/global/legacyInvoices/legacyInvoicesProvider.vue";
import LegacyInvoicesTable from "@/components/app/global/legacyInvoices/legacyInvoicesTable.vue";
import ProductsSearch from "@/components/app/admin/catalogue/products/productSearchTable.vue";
import ProductsTable from "@/components/app/global/contractProducts/productsTable.vue";
import TicketsTable from "@/components/app/global/tickets/ticketsTable.vue";
import UserSearch from "@/components/app/global/user/userSearchTable.vue";

@Component({
  components: {
    "step-mappings-provider": () =>
      import("@/components/app/admin/imports/stepMappingsProvider.vue"),
    "step-mappings-table": () =>
      import("@/components/app/admin/imports/stepMappingsTable.vue")
  }
})
export default class ImportStepBody extends Vue {
  // Inject

  @Inject({ from: "importProvider" })
  public readonly $importProvider!: () => IImportProviderData;

  @Inject({ from: "importMethods" })
  public readonly $importMethods!: IImportProviderMethods;

  // Props

  @Prop({ required: true })
  public step!: IImportStep;

  // Data

  public get $import() {
    return this.$importProvider();
  }

  // Computed

  public get exportingMsgPath() {
    if (this.$_.isNull(this.step.estimated_completed_at))
      return "_sentence.imports.exporting_status_summary_completion_unknown_msg";
    return "_sentence.imports.exporting_status_summary_msg";
  }

  public get importingMsgPath() {
    if (this.$_.isNull(this.step.estimated_completed_at))
      return "_sentence.imports.importing_status_summary_completion_unknown_msg";
    return "_sentence.imports.importing_status_summary_msg";
  }

  public get timeRemaining() {
    return this.$moment
      .duration(this.step.estimated_remaining_seconds, "seconds")
      .humanize();
  }

  public get isExporting() {
    return [ImportStatusCodes.EXPORTING].includes(this.step.status.code);
  }

  public get isImporting() {
    return [ImportStatusCodes.IN_PROGRESS].includes(this.step.status.code);
  }

  public get showMappings() {
    return [ImportStatusCodes.MAPPINGS_REQUIRED].includes(
      this.step.status.code
    );
  }

  public get showStagedRecords() {
    return [
      ImportStatusCodes.IN_PROGRESS,
      ImportStatusCodes.STAGED,
      ImportStatusCodes.COMPLETE
    ].includes(this.step.status.code);
  }

  public get component() {
    const manualFilters = {
      with_staged_imports: 1,
      "filter[staged_import]": 1,
      "filter[import_id]": this.$import.import.id
    };

    switch (this.step.code) {
      case ImportStepCodes.PRODUCTS:
        return {
          render: (h: CreateElement) =>
            h(ProductsSearch, {
              props: {
                brandId: this.$import.import.brand_id,
                ignoreHistory: true,
                limit: 5,
                manualFilters
              }
            })
        };
      case ImportStepCodes.CLIENTS:
        return {
          render: (h: CreateElement) =>
            h(ClientSearch, {
              props: {
                brandId: this.$import.import.brand_id,
                showGuestsToggle: false,
                manualFilters
              }
            })
        };
      case ImportStepCodes.AFFILIATE_ACCOUNTS:
        return {
          render: (h: CreateElement) =>
            h(
              AffiliatesProvider,
              {
                props: {
                  dataScope: "$staged_affiliates",
                  ignoreHistory: true,
                  manualFilters,
                  limit: 5
                }
              },
              [h(AffiliatesTable)]
            )
        };
      case ImportStepCodes.USERS:
        return {
          render: (h: CreateElement) =>
            h(UserSearch, {
              props: {
                dataScope: "$staged_user_search",
                showTableHead: false,
                manualFilters
              }
            })
        };
      case ImportStepCodes.CLIENT_PAYMENT_DETAILS:
        return {
          render: (h: CreateElement) =>
            h(BrandPaymentMethodsList, {
              props: {
                brandId: this.$import.import.brand_id,
                dataScope: "$staged_payment_methods",
                showTableHead: false,
                manualFilters
              }
            })
        };
      case ImportStepCodes.CONTRACTS:
        return {
          render: (h: CreateElement) =>
            h(ProductsTable, {
              props: {
                dataScope: "$staged_contracts",
                showTableHead: false,
                id: "CPT1",
                manualFilters,
                ignoreHistory: true,
                limit: 5,
                showWhenPurchased: false,
                isStaged: true
              }
            })
        };
      case ImportStepCodes.INVOICES:
        return {
          render: (h: CreateElement) =>
            h(
              LegacyInvoicesProvider,
              {
                props: {
                  dataScope: "$staged_legacy_invoices",
                  ignoreHistory: true,
                  limit: 5,
                  manualFilters
                }
              },
              [h(LegacyInvoicesTable)]
            )
        };
      case ImportStepCodes.TICKETS:
        return {
          render: (h: CreateElement) =>
            h(TicketsTable, {
              props: {
                dataScope: "$staged_tickets",
                setDefaultStatusFilter: false,
                manualFilters,
                ignoreHistory: true,
                limit: 5
              }
            })
        };
      default:
        return {};
    }
  }
}
